import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { installFrontendEnabled, useOptionalInstallStatus } from '@/install-bridge'

interface InstallGuardProps {
  children: ReactNode
  allowInstallPage?: boolean
}

export function InstallGuard({ children, allowInstallPage = false }: InstallGuardProps) {
  if (!installFrontendEnabled) {
    return <>{children}</>
  }

  const location = useLocation()
  const { data, isLoading, isError } = useOptionalInstallStatus()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
          <p className="mt-3 text-sm text-muted-foreground">正在检查安装状态...</p>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-md rounded-[1.5rem] border border-border/80 bg-card/88 p-6 text-center">
          <h1 className="text-lg font-semibold text-foreground">无法获取安装状态</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            请确认后端服务已启动，并检查安装接口是否可访问。
          </p>
        </div>
      </div>
    )
  }

  const installed = Boolean(data?.installed)
  const isInstallPath = location.pathname === '/install'

  if (!installed && !allowInstallPage && !isInstallPath) {
    return <Navigate to="/install" replace />
  }

  if (installed && (allowInstallPage || isInstallPath)) {
    return <Navigate to="/" replace />
  }

  return (
    <>
      {installed ? (
        <div className="border-b border-amber-200 bg-amber-50/90 px-4 py-3 text-center text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          安装前端仍已启用。若不再需要安装界面，请删除前端的 <code className="mx-1 rounded bg-black/5 px-1.5 py-0.5 dark:bg-white/10">src/install</code> 目录后重新构建。
        </div>
      ) : null}
      {children}
    </>
  )
}
